import React, { createContext, useContext, useState, useEffect, startTransition } from 'react';
import axios from 'axios';
import { authAPI, bdaAuthAPI, serviceProviderPortalAPI } from '../services/api';
import apiClient from '../services/api';

// Create Authentication Context
export const AuthContext = createContext(null);

// Custom hook to use Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => {
    const token = localStorage.getItem('auth_token');
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const userRole = localStorage.getItem('userRole');
    
    return {
      user,
      token,
      isAuthenticated: !!token,
      userRole: userRole || 'admin',
      loading: true
    };
  });

  // Computed properties for feature visibility
  const getFeatureVisibility = () => {
    const user = authState.user;
    if (!user) {
      if (process.env.NODE_ENV === 'development') {
        //console.log('AuthContext: No user data available for feature visibility');
      }
      return {
        bookMyOpVisible: false,
        easyOpVisible: false
      };
    }

    // For service providers, check the visibility flags from profile
    if (authState.userRole === 'service-provider' || authState.userRole === 'sp_staff') {
      const visibility = {
        bookMyOpVisible: user.visible_in_book_my_op !== false, // Default to true if not specified
        easyOpVisible: user.visible_in_easy_op !== false // Default to true if not specified
      };
      
      if (process.env.NODE_ENV === 'development') {
        //console.log('AuthContext: Service Provider Feature Visibility:', {
        //   userId: user.id,
        //   userRole: authState.userRole,
        //   rawFlags: {
        //     visible_in_book_my_op: user.visible_in_book_my_op,
        //     visible_in_easy_op: user.visible_in_easy_op
        //   },
        //   computedVisibility: visibility
        // });
      }
      
      return visibility;
    }

    // For other roles (admin, bda), return default visibility
    if (process.env.NODE_ENV === 'development') {
      //console.log('AuthContext: Non-SP user, returning default visibility for role:', authState.userRole);
    }
    return {
      bookMyOpVisible: true,
      easyOpVisible: true
    };
  };

  // Helper methods for feature checking
  const canAccessBookMyOp = () => {
    return getFeatureVisibility().bookMyOpVisible;
  };

  const canAccessEasyOp = () => {
    return getFeatureVisibility().easyOpVisible;
  };

  // Helper to check if any features are restricted
  const hasRestrictedAccess = () => {
    const visibility = getFeatureVisibility();
    return !visibility.bookMyOpVisible || !visibility.easyOpVisible;
  };

  // Debug helper for development
  const getFeatureDebugInfo = () => {
    const user = authState.user;
    const visibility = getFeatureVisibility();
    
    return {
      userRole: authState.userRole,
      userId: user?.id,
      userName: user?.name,
      profileFlags: {
        visible_in_book_my_op: user?.visible_in_book_my_op,
        visible_in_easy_op: user?.visible_in_easy_op
      },
      computedVisibility: visibility,
      hasRestrictedAccess: hasRestrictedAccess()
    };
  };

  // Set token in Axios headers
  const setAuthToken = (token) => {
    if (token && token !== 'null' && token !== 'undefined') {
      // The token from the backend already includes 'Bearer ' prefix
      // Just ensure it's properly formatted
      const formattedToken = token.trim();
      
      // Validate token format
      if (!formattedToken.match(/^Bearer [A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*$/)) {
        console.error('Invalid token format:', formattedToken.substring(0, 20) + '...');
        return null;
      }
      
      // Set for both axios default and apiClient instance
      axios.defaults.headers.common['Authorization'] = formattedToken;
      apiClient.defaults.headers.common['Authorization'] = formattedToken;
      
      // Store the formatted token in localStorage
      localStorage.setItem('auth_token', formattedToken);
    
      return formattedToken;
    }
    // Remove token from headers if not valid
    delete axios.defaults.headers.common['Authorization'];
    delete apiClient.defaults.headers.common['Authorization'];
    localStorage.removeItem('auth_token');
    //console.log('Auth token removed');
    return null;
  };

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        const userRole = localStorage.getItem('userRole');
        const loginTimestamp = localStorage.getItem('loginTimestamp');
        
        //console.log('Initializing auth - token exists:', !!token);
        
        if (!token || !user) {
          startTransition(() => {
            setAuthState({
              user: null,
              token: null,
              isAuthenticated: false,
              userRole: userRole || 'admin',
              loading: false
            });
          });
          return;
        }
        
        // Format and set token
        const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        setAuthToken(formattedToken);
        
        const currentTime = Date.now();
        const recentLogin = loginTimestamp && (currentTime - parseInt(loginTimestamp)) < 5000;
        
        // Skip profile fetch for recent logins
        if (recentLogin) {
          //console.log('Recent login detected, using cached user data');
          setAuthState({
            user,
            token: formattedToken,
            isAuthenticated: true,
            userRole: userRole || 'admin',
            loading: false
          });
          return;
        }
        
        // Try to fetch fresh user data based on role
        try {
          let userData = null;
          
          if (userRole === 'bda') {
            const response = await bdaAuthAPI.getProfile();
            userData = response.data;
          } else if (userRole === 'service-provider' || userRole === 'sp_staff') {
            // For service providers, fetch the full profile to get visibility flags
            const response = await serviceProviderPortalAPI.getProfile();
            userData = response.data;
            
            // Log feature visibility flags for service providers
            console.log('Service Provider Feature Visibility:', {
              userId: userData?.id,
              userName: userData?.name,
              visible_in_book_my_op: userData?.visible_in_book_my_op,
              visible_in_easy_op: userData?.visible_in_easy_op,
              userType: userData?.user_type
            });
          } else {
            // Default to admin
            const response = await authAPI.me();
            userData = response.data;
          }
          
          if (userData) {
            localStorage.setItem('user', JSON.stringify(userData));
            startTransition(() => {
              setAuthState({
                user: userData,
                token: formattedToken,
                isAuthenticated: true, 
                userRole: userRole || 'admin',
                loading: false
              });
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          
          // Handle token expiration or authentication errors
          if (error.response?.status === 401) {
            // Try token refresh if we have a refresh token
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              try {
                //console.log('Attempting token refresh');
                const response = await authAPI.refreshToken({ refresh_token: refreshToken });
                const newToken = response.data.token || response.data.access;
                const formattedNewToken = newToken.startsWith('Bearer ') ? newToken : `Bearer ${newToken}`;
                
                localStorage.setItem('auth_token', formattedNewToken);
                setAuthToken(formattedNewToken);
                
                // Successfully refreshed, keep user data
                setAuthState({
                  user,
                  token: formattedNewToken,
                  isAuthenticated: true,
                  userRole: userRole || 'admin',
                  loading: false
                });
                return;
              } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
              }
            }
            
            // Clear auth state if refresh failed or no refresh token
            clearAuthState();
          } else {
            // Other error, keep token but update loading state
            setAuthState(prev => ({
              ...prev,
              loading: false
            }));
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuthState();
      }
    };
    
    initAuth();
  }, []);

  // Clear authentication state
  const clearAuthState = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('loginTimestamp');
    localStorage.removeItem('sp_staff_user');
    setAuthToken(null);
    
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      userRole: 'admin',
      loading: false
    });
  };

  // Admin login function
  const adminLogin = async (email, password) => {
    try {
      //console.log("Admin login attempt for:", email);
      
      // Get CSRF token first
      try {
        await authAPI.refreshCSRFToken();
      } catch (csrfError) {
        console.error("CSRF token refresh failed:", csrfError);
        // Continue with login attempt even if CSRF refresh fails
      }
      
      // Login request
      const response = await authAPI.login({ email, password });
      
      // Ensure token has Bearer prefix
      const token = response.data.token;
      const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const refreshToken = response.data.refresh_token;
      const userData = response.data.user;
      
      // Save to localStorage
      localStorage.setItem('auth_token', formattedToken);
      localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('userRole', 'admin');
      localStorage.setItem('loginTimestamp', Date.now().toString());
      
      // Set token in axios headers
      setAuthToken(formattedToken);
      
      // Update auth state
      setAuthState({
        user: userData,
        token: formattedToken,
        isAuthenticated: true,
        userRole: 'admin',
        loading: false
      });
      
      return { success: true };
    } catch (error) {
      console.error('Admin login error:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
      return { success: false, message: errorMessage };
    }
  };
  
  // BDA login function
  const bdaLogin = async (email, password) => {
    try {
      //console.log("BDA login attempt for:", email);
      
      // Get CSRF token first
      await bdaAuthAPI.refreshCSRFToken();
      
      // Login request with username parameter
      const response = await bdaAuthAPI.login({ username: email, password });
      //console.log("BDA login response:", response.data);
      
      // Process response
      const token = response.data.token;
      const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      
      // Save auth data
      localStorage.setItem('auth_token', formattedToken);
      localStorage.setItem('user', JSON.stringify({
        ...response.data,
        role: 'bda'
      }));
      localStorage.setItem('userRole', 'bda');
      localStorage.setItem('loginTimestamp', Date.now().toString());
      
      // Set token in headers
      setAuthToken(formattedToken);
      
      // Update auth state
      setAuthState({
        user: response.data,
        token: formattedToken,
        isAuthenticated: true,
        userRole: 'bda',
        loading: false
      });
      
      return {
        success: true,
        ...response.data
      };
    } catch (error) {
      console.error('BDA Login error:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('No response from server. Please check your internet connection.');
      }
    }
  };
  
  // Service Provider login function
  const serviceProviderLogin = async (email, password) => {
    try {
      //console.log("Attempting service provider login for:", email);
      
      // Get CSRF token
      try {
        await serviceProviderPortalAPI.csrf();
      } catch (csrfError) {
        console.error("CSRF token refresh failed:", csrfError);
        // Continue with login attempt even if CSRF refresh fails
      }
      
      //console.log("Making login request to:", "/api/sp/auth/login/");
      const response = await serviceProviderPortalAPI.login({ email, password });
      
      // Process response
      const token = response.data.token;
      const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const refreshToken = response.data.refresh_token;
      const userData = response.data.user;
      
      // Log feature visibility on login
      //console.log('Service Provider Login - Feature Visibility:', {
      //   userId: userData?.id,
      //   userName: userData?.name,
      //   userType: userData?.user_type,
      //   visible_in_book_my_op: userData?.visible_in_book_my_op,
      //   visible_in_easy_op: userData?.visible_in_easy_op
      // });

      // Save auth data
      localStorage.setItem('auth_token', formattedToken);
      localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      const userRole = userData.user_type === 'sp_admin' ? 'service-provider' : 'sp_staff';
      localStorage.setItem('userRole', userRole);
      localStorage.setItem('loginTimestamp', Date.now().toString());
      
      // Store sp_staff_user data for staff users
      if (userData.user_type === 'sp_staff') {
        localStorage.setItem('sp_staff_user', JSON.stringify({
          doctor_id: userData.doctor_id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          user_type: userData.user_type,
          service_provider: userData.service_provider
        }));
      }
      
      console.log('Service Provider Login - Setting userRole:', {
        userType: userData.user_type,
        userRole: userRole,
        userId: userData.id,
        userEmail: userData.email
      });
      
      // Set token in headers
      setAuthToken(formattedToken);
      
      // Immediately fetch full service provider profile so provider_type etc. are available without refresh
      try {
        const profileResp = await serviceProviderPortalAPI.getProfile();
        const profileData = profileResp?.data || userData;
        localStorage.setItem('user', JSON.stringify(profileData));
        const finalUserRole = userData.user_type === 'sp_admin' ? 'service-provider' : 'sp_staff';
        setAuthState({
          user: profileData,
          token: formattedToken,
          isAuthenticated: true,
          userRole: finalUserRole,
          loading: false
        });
        console.log('Service Provider Login - Profile loaded and auth state updated');
        return {
          success: true,
          data: profileData
        };
      } catch (profileError) {
        console.error('Failed to load full service provider profile after login, using minimal user:', profileError);
        const finalUserRole = userData.user_type === 'sp_admin' ? 'service-provider' : 'sp_staff';
        setAuthState({
          user: userData,
          token: formattedToken,
          isAuthenticated: true,
          userRole: finalUserRole,
          loading: false
        });
        return {
          success: true,
          data: userData
        };
      }
    } catch (error) {
      console.error('Service provider login error:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      if (error.request) {
        console.error('Error request:', error.request);
      }
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  // Fetch and update service provider profile with visibility flags
  const fetchServiceProviderProfile = async () => {
    try {
      const response = await serviceProviderPortalAPI.getProfile();
      const profileData = response.data;
      
      // Extract visibility flags from profile
      const visibilityFlags = {
        visible_in_book_my_op: profileData.visible_in_book_my_op,
        visible_in_easy_op: profileData.visible_in_easy_op
      };
      
      // Update user data with profile information including visibility flags
      const updatedUserData = {
        ...authState.user,
        ...profileData,
        ...visibilityFlags
      };
      
      // Store updated user data in localStorage
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      
      // Update auth state
      setAuthState(prev => ({
        ...prev,
        user: updatedUserData
      }));
      
      console.log('Service Provider Profile Updated:', {
        userId: updatedUserData.id,
        userName: updatedUserData.name,
        visibilityFlags: visibilityFlags
      });
      
      return updatedUserData;
    } catch (error) {
      console.error('Error fetching service provider profile:', error);
      throw error;
    }
  };
  
  // Logout function
  const logout = async () => {
    try {
      //console.log('Logging out user...');
      
      // Get userRole before clearing
      const userRole = localStorage.getItem('userRole') || 'admin';
      
      // Try to call backend logout API based on user type
      try {
        if (userRole === 'bda') {
          await bdaAuthAPI.logout();
        } else if (userRole === 'service-provider' || userRole === 'sp_staff') {
          await serviceProviderPortalAPI.logout();
        } else {
          await authAPI.logout();
        }
        //console.log('Server logout successful');
      } catch (error) {
        console.error('Server logout failed, but client logout will proceed', error);
      }
      
      // Clear auth state regardless of server response
      clearAuthState();
      
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if server logout fails
      clearAuthState();
      return false;
    }
  };

  // Handle user update
  const updateUser = (updates) => {
    try {
      const currentUser = authState.user || JSON.parse(localStorage.getItem('user') || 'null');
      if (!currentUser) return;
      
      // Update user data
      const updatedUser = { ...currentUser, ...updates };
      
      // Save to localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Update auth state
      setAuthState(prev => ({
        ...prev,
        user: updatedUser
      }));
      
      return updatedUser;
    } catch (error) {
      console.error('Error updating user data:', error);
      return null;
    }
  };

  // Context value
  const contextValue = {
    user: authState.user,
    token: authState.token,
    isAuthenticated: authState.isAuthenticated,
    userRole: authState.userRole,
    loading: authState.loading,
    
    // Auth methods
    adminLogin,
    bdaLogin,
    serviceProviderLogin,
    logout,
    updateUser,
    
    // Feature visibility methods
    getFeatureVisibility,
    canAccessBookMyOp,
    canAccessEasyOp,
    hasRestrictedAccess,
    getFeatureDebugInfo,
    
    // Service provider specific methods
    fetchServiceProviderProfile,
    
    // Token management
    setAuthToken
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}; 